/**
 * Shared event section + card components used by both
 * EventsListSheet (bottom sheet) and EventsFullScreen (full-screen overlay).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons/faCalendarDays";
import { faBolt } from "@fortawesome/pro-solid-svg-icons/faBolt";
import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { cn } from "@wondermaps/ui";
import { contrastText } from "../../../lib/color-utils";
import type { VisitorEvent } from "../../../hooks/visitor/useVisitorEvents";
import {
  formatEventTime,
  formatEventSchedule,
  formatUntilTime,
  getRelativeDateLabel,
  getNextOccurrence,
} from "../../../lib/event-schedule";
import { analytics } from "../../../lib/analytics";
import { ExpandableText } from "../ExpandableText";

// Re-export icons for consumers that need section defaults
export { faCalendarDays, faBolt, faClock };

/** Shared body content for events views (sheet + full-screen). */
export interface EventsBodyProps {
  happeningNowEvents: VisitorEvent[];
  todaysEvents: VisitorEvent[];
  upcomingEvents: VisitorEvent[];
  onNavigateToPoi?: (poiId: string, event: VisitorEvent) => void;
  poiNames?: Map<string, string>;
}

export function EventsBody({
  happeningNowEvents,
  todaysEvents,
  upcomingEvents,
  onNavigateToPoi,
  poiNames,
}: EventsBodyProps) {
  const { t } = useTranslation("events");
  const totalEvents =
    happeningNowEvents.length + todaysEvents.length + upcomingEvents.length;
  const todayUpcomingOnly = todaysEvents.filter(
    (e) => e.status !== "happening-now"
  );

  if (totalEvents === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
        <FontAwesomeIcon icon={faCalendarDays} className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-center">{t("noUpcoming")}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {happeningNowEvents.length > 0 && (
        <EventSection
          title={t("happeningNow")}
          icon={<FontAwesomeIcon icon={faBolt} className="h-4 w-4 text-green-600" />}
          events={happeningNowEvents}
          variant="now"
          onNavigateToPoi={onNavigateToPoi}
          poiNames={poiNames}
        />
      )}
      {todayUpcomingOnly.length > 0 && (
        <EventSection
          title={t("comingUpToday")}
          icon={<FontAwesomeIcon icon={faClock} className="h-4 w-4 text-blue-600" />}
          events={todayUpcomingOnly}
          variant="today"
          onNavigateToPoi={onNavigateToPoi}
          poiNames={poiNames}
        />
      )}
      {upcomingEvents.length > 0 && (
        <EventSection
          title={t("upcoming")}
          icon={<FontAwesomeIcon icon={faCalendarDays} className="h-4 w-4 text-muted-foreground" />}
          events={upcomingEvents}
          variant="upcoming"
          onNavigateToPoi={onNavigateToPoi}
          poiNames={poiNames}
        />
      )}
    </div>
  );
}

export interface EventSectionProps {
  title: string;
  icon: React.ReactNode;
  events: VisitorEvent[];
  variant: "now" | "today" | "upcoming";
  onNavigateToPoi?: (poiId: string, event: VisitorEvent) => void;
  poiNames?: Map<string, string>;
}

export function EventSection({
  title,
  icon,
  events,
  variant,
  onNavigateToPoi,
  poiNames,
}: EventSectionProps) {
  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-semibold text-sm">{title}</h3>
        <span className="text-xs text-muted-foreground">({events.length})</span>
      </div>

      {/* Event Cards */}
      <div className="space-y-2">
        {events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            variant={variant}
            onNavigateToPoi={onNavigateToPoi}
            poiName={event.poiId ? poiNames?.get(event.poiId) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

interface EventCardProps {
  event: VisitorEvent;
  variant: "now" | "today" | "upcoming";
  onNavigateToPoi?: (poiId: string, event: VisitorEvent) => void;
  poiName?: string;
}

function EventCard({
  event,
  variant,
  onNavigateToPoi,
  poiName,
}: EventCardProps) {
  const { t, i18n } = useTranslation("events");
  const locale = i18n.language;
  const nextOccurrence = getNextOccurrence(event);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descCanExpand, setDescCanExpand] = useState(false);
  const cardIsToggleable = !!event.description && descCanExpand;
  const toggleDesc = () => setDescExpanded((v) => !v);

  return (
    <div
      role={cardIsToggleable ? "button" : undefined}
      tabIndex={cardIsToggleable ? 0 : undefined}
      onClick={cardIsToggleable ? toggleDesc : undefined}
      onKeyDown={
        cardIsToggleable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleDesc();
              }
            }
          : undefined
      }
      className={cn(
        "rounded-lg border p-3",
        cardIsToggleable && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        variant === "now" && "border-green-200 bg-green-50"
      )}
    >
      {/* Event name + sponsor badge + status badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-medium">{event.name}</span>
          {event.sponsorRef?.sponsorTierColor && (
            <span
              className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold"
              style={{
                backgroundColor: event.sponsorRef.sponsorTierColor,
                color: contrastText(event.sponsorRef.sponsorTierColor),
              }}
            >
              {event.sponsorRef.sponsorLogoUrl && (
                <img
                  src={event.sponsorRef.sponsorLogoUrl}
                  alt=""
                  className="h-3 w-3 rounded-sm object-contain"
                />
              )}
              {event.sponsorRef.sponsorName ? t("sponsoredBy", { name: event.sponsorRef.sponsorName }) : null}
            </span>
          )}
        </div>
        {variant === "now" && (
          <span className="shrink-0 px-1.5 py-0.5 bg-green-600 text-white rounded text-xs font-medium">
            {t("now")}
          </span>
        )}
      </div>

      {/* Time info — next occurrence */}
      <div className="text-sm text-muted-foreground mt-1">
        {variant === "now" ? (
          formatUntilTime(event.endTime, t, locale)
        ) : variant === "today" ? (
          <>{t("startsAt", { time: formatEventTime(event, locale).split(" - ")[0] })}</>
        ) : nextOccurrence ? (
          <>
            {getRelativeDateLabel(nextOccurrence.date, undefined, t)} •{" "}
            {formatEventTime(event, locale)}
          </>
        ) : (
          formatEventSchedule(event, t)
        )}
      </div>

      {/* Multi-day schedule — pattern + range when event recurs */}
      {event.scheduleType !== "single" && nextOccurrence && (
        <div className="text-xs text-muted-foreground/80 mt-0.5">
          {formatEventSchedule(event, t)}
        </div>
      )}

      {/* Description (truncated, expandable) — also toggled by card click */}
      {event.description && (
        <ExpandableText
          text={event.description}
          lines={2}
          className="mt-1"
          textClassName="text-sm text-muted-foreground"
          expanded={descExpanded}
          onExpandedChange={setDescExpanded}
          onCanExpandChange={setDescCanExpand}
        />
      )}

      {/* Headliner */}
      {event.headlinerName && (
        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
          {event.headlinerLogoUrl && (
            <img
              src={event.headlinerLogoUrl}
              alt=""
              className="h-4 w-4 rounded object-cover"
            />
          )}
          <span>{t("featuring", { name: event.headlinerName })}</span>
        </div>
      )}

      {/* Event Link */}
      {event.eventUrl && (
        <a
          href={event.eventUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            analytics.eventLinkClick({
              event_id: event.id,
              event_name: event.name,
              url: event.eventUrl!,
              poi_id: event.poiId ?? undefined,
            });
          }}
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" />
          <span>{event.eventUrlLabel || t("moreInfo", { defaultValue: "More Info" })}</span>
        </a>
      )}

      {/* POI Link */}
      {event.poiId && poiName && onNavigateToPoi && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigateToPoi(event.poiId!, event);
          }}
          className="mt-2 flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <FontAwesomeIcon icon={faLocationDot} className="h-3.5 w-3.5" />
          <span>{poiName}</span>
        </button>
      )}
    </div>
  );
}
